import {
  convertToDecimals,
  IAdminRefundRequests,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  RefundStatus,
  TimeFormat,
  TypeOfUser,
} from '@pharmaplan/common';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';
import { removeBasedOnPermissions } from '../../../helpers/permissionsHelper';
import { DynamicTableCell } from '../../../helpers/Constants';
import { getDayMonthDateYear, getHourMinute } from '../../Reports/helpers';
import approveBookingIcon from '../../../assets/svg/approveBooking.svg';
import rejectBookingIcon from '../../../assets/svg/rejectBooking.svg';
import strings from '../../../localization';

import styles from './styles';

const { Approved, Rejected, Pending } = RefundStatus;
const { customLink, text, chip } = DynamicTableCell;

export const refundStatusLabelMap = {
  [Pending]: strings.pending,
  [Approved]: strings.approved,
  [Rejected]: strings.rejected,
};

const statusStyleMap = {
  [Pending]: styles.pending,
  [Approved]: styles.approved,
  [Rejected]: styles.rejected,
};

export const cancellationRefundHeader = (
  can: (params: PermissionsOfAdmin) => boolean,
) => {
  const arr = [
    {
      key: '0',
      label: strings.requestId,
      sortingKey: 'code',
    },
    {
      key: '1',
      label: strings.formatString(strings.dateTimeSuffix, strings.requested),
      sortingKey: 'requestedDate',
    },
    {
      key: '2',
      label: strings.requestedBy,
      sortingKey: 'pharmacyName',
    },
    {
      key: '4',
      label: strings.amount,
      sortingKey: 'amount',
    },
    {
      key: PermissionsRemovalKeys.Actions,
      label: strings.action,
      sortingKey: 'status',
    },
  ];

  return removeBasedOnPermissions(arr, [PermissionsOfAdmin.PendingRefunds], can);
};

export const pendingRefundRowConfig = (
  list: Array<IAdminRefundRequests>,
  can: (params: PermissionsOfAdmin) => boolean,
  goToLink: (codeId: string) => () => void,
  goToUser: IUserProfileLinkSignature,
  handleApprove: (id: string) => () => void,
  handleReject: (id: string) => () => void,
) =>
  list?.map((item) => {
    const {
      refundRequestId,
      requestedDate,
      pharmacyName,
      pharmacyId,
      subscriptionId,
      status,
      amount,
      code,
    } = item ?? {};

    const canApproveReject = can(PermissionsOfAdmin.PendingRefunds);

    const date = `${getDayMonthDateYear(requestedDate)} | ${getHourMinute(requestedDate, TimeFormat.twelveHours)}`;
    const isPending = status === Pending;
    let finalObj = null;

    const statusObj = {
      key: `3-${refundRequestId}`,
      value: {
        label: refundStatusLabelMap[status as keyof typeof refundStatusLabelMap],
        style: statusStyleMap[status as keyof typeof statusStyleMap],
      },
      type: chip,
    };

    const confirmationObj = {
      key: `6-${refundRequestId}`,
      value: [
        {
          key: '1',
          icon: approveBookingIcon,
          tooltip: strings.approve,
          onClick:
            handleApprove?.(refundRequestId ?? ''),
        },
        {
          key: '2',
          tooltip: strings.reject,
          icon: rejectBookingIcon,
          onClick:
            handleReject?.(refundRequestId ?? ''),
        },
      ],
      type: DynamicTableCell.bar,
    };

    if (isPending && canApproveReject) {
      finalObj = confirmationObj;
    } else {
      finalObj = statusObj;
    }

    return {
      key: subscriptionId,
      data: [
        {
          key: `0-${refundRequestId}`,
          value: {
            label: code,
            handlePress: goToLink?.(refundRequestId),
          },
          type: customLink,
        },
        {
          key: `${refundRequestId}`,
          value: date,
          type: text,
        },
        {
          key: `4-${refundRequestId}`,
          value: {
            label: pharmacyName,
            handlePress: goToUser?.({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
            }),
          },
          type: customLink,
        },
        {
          key: `5-${refundRequestId}`,
          value: `$${convertToDecimals(amount)}`,
          type: text,
        },
        finalObj,
      ],
    };
  });
